import { useRef, useEffect, useCallback } from 'react';

import {
  useStripeCreateMutation,
  StripeCreateRequest,
  StripeCreateResponse,
} from '@/api/payments/stripeCreate';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { useCartContext } from '@/services/cart/CartContext';
import { RecaptchaLibToken, RecaptchaLibVersion } from '@/libs/recaptcha-lib/types/types';
import { useSite, SiteData, SiteUrls } from '@/services/site/useSite';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';
import { setTransactionId } from '@/features/paymentProcess/transactionSlice';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

const buildCreatePayload = (
  paymentSelectState: PaymentSelectState,
  userDetailsState: UserDetailsState,
  client: string,
  siteData: SiteData,
  siteUrls: SiteUrls,
  recaptchaToken: RecaptchaLibToken,
  reCaptchaVersion: RecaptchaLibVersion,
  isPayin: boolean,
  campaignCode: string | null,
  cartId?: string,
): StripeCreateRequest => ({
  client,
  order_reference: paymentSelectState.orderReference,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  giftaid: Number(paymentSelectState.giftaid),
  firstName: userDetailsState.firstName,
  lastName: userDetailsState.lastName,
  email: userDetailsState.email,
  address1: userDetailsState.address1,
  address2: userDetailsState.address2,
  address3: userDetailsState.address3,
  town: userDetailsState.town,
  postcode: userDetailsState.postcode,
  country: userDetailsState.country,
  campaign: campaignCode ?? siteData.campaign.name,
  transSource: siteData.campaign.transSource,
  transSourceUrl: siteUrls.transSourceUrl,
  transType: isPayin ? 'payin' : siteData.campaign.transType,
  recaptcha_token: recaptchaToken,
  cartId: cartId ?? siteData.defaultCart,
  recaptcha_version: reCaptchaVersion,
  ...(!!paymentSelectState.fixedTransactionId && { transactionId: paymentSelectState.fixedTransactionId }),
});

export function useStripeCreate() {
  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const { cartId, getCartFinancialAttribution } = useCartContext();

  const onSuccess = useRef<((data?: StripeCreateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const [stripeCreateTrigger, {
    isLoading,
    data,
    error,
  }] = useStripeCreateMutation();

  const { getClientId } = useClient();
  const { getSiteData, getSiteUrls } = useSite();
  const { selectedJourney } = useJourneyOptions();
  const isPayin = selectedJourney === 'payin';

  // Trigger payment-service create
  const triggerCreateApi = useCallback(async (
    params: {
      recaptchaToken: RecaptchaLibToken;
      reCaptchaVersion: RecaptchaLibVersion,
      onSuccess?: (data?: StripeCreateResponse) => void;
      onError?: (error?: unknown) => void;
    }
  ) => {
    onSuccess.current = params?.onSuccess;
    onError.current = params?.onError;

    try {
      const payload = buildCreatePayload(
        paymentSelectState,
        userDetailsState,
        getClientId(),
        getSiteData(),
        getSiteUrls(),
        params.recaptchaToken,
        params.reCaptchaVersion,
        isPayin,
        getCartFinancialAttribution()?.campaignCode ?? null,
        cartId,
      );
      await stripeCreateTrigger(payload);
    } catch (err) {
      logger.error('Error calling stripeCreateTrigger:', err);
      if (onError.current) {
        onError.current(err);
      }
      // NOTE: No reCAPTCHA specific logic here as it's handled globally by the baseQuery
    }
  }, [
    paymentSelectState,
    userDetailsState,
    getClientId,
    getSiteData,
    getSiteUrls,
    stripeCreateTrigger,
    getCartFinancialAttribution,
    isPayin,
    cartId,
  ]);

  // Handle payment-service create complete
  useEffect(() => {
    // Wait until payment-service create is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service create errors (any error, reCAPTCHA handled globally)
    if (!!error || data?.message !== 'success' || !data?.data?.transactionId) {
      logger.error('Error in Stripe transaction useEffect:', error);

      // Call the onError callback if provided for any error
      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    // Dispatch payment-service create data to transactionState
    dispatch(setTransactionId(data.data.transactionId));

    if (onSuccess.current) {
      onSuccess.current(data);
    }
  }, [isLoading, data, error, onSuccess, onError, dispatch]);

  return {
    triggerCreateApi,
    createIsLoading: isLoading,
    createData: data,
    createError: error,
  };
}
